import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CloudArrowDown, Pause, Play } from "@phosphor-icons/react";
import { adminApi, getErrorMessage } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { Card, Pill, btnGhost, btnPrimary, num } from "./ui";

const PHASES = [
  ["discover", "Discover claims"], ["extract", "Read repair orders"], ["classify_text", "Classify issues"],
  ["taxonomy", "Standardise repairs"], ["stats", "Benchmarks & validation"],
];
const STATUS_TONE = { running: "sky", done: "green", paused: "amber", failed: "red", not_started: "neutral" };

function Stepper({ job }) {
  const idx = job.status === "done" ? PHASES.length : PHASES.findIndex(([k]) => k === job.phase);
  return (
    <ol className="grid grid-cols-5 gap-2" data-testid="wi-phase-stepper">
      {PHASES.map(([k, label], i) => {
        const state = i < idx ? "done" : i === idx && job.status === "running" ? "active" : "todo";
        return (
          <li key={k} className={`rounded-lg border px-2.5 py-2 text-[0.7rem] ${state === "done" ? "border-[#73bf44]/40 text-[#73bf44] bg-[#73bf44]/[0.06]" : state === "active" ? "border-sky-500/40 text-sky-300 bg-sky-500/[0.06]" : "border-neutral-800 text-neutral-500"}`}>
            <span className="font-bold tabular-nums mr-1">{i + 1}</span>{label}
          </li>
        );
      })}
    </ol>
  );
}

export function ImportJobCard({ onChange }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => adminApi.get("/admin/wi/status").then(({ data }) => setSt(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  usePolling(load, st?.job?.status === "running" ? 5000 : 30000);
  useEffect(() => { if (st?.job?.status === "done") onChange?.(); }, [st?.job?.status, onChange]);

  const act = async (path, msg) => {
    setBusy(true);
    try {
      const { data } = await adminApi.post(path);
      if (data.started === false) toast.info(data.reason || "Import already running"); else toast.success(msg);
      load();
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  if (!st) return <Card title="Historical import" icon={CloudArrowDown}><div className="h-24 animate-pulse bg-white/[0.02] rounded-xl" /></Card>;
  const { job, extraction: ex } = st;
  const totalPdf = (ex.done || 0) + (ex.pending || 0) + (ex.failed || 0);
  const pctDone = totalPdf ? Math.round(((ex.done || 0) / totalPdf) * 100) : 0;
  const running = job.status === "running";
  return (
    <Card title="Historical import" icon={CloudArrowDown} testId="wi-import-card" actions={
      <>
        <Pill tone={STATUS_TONE[job.status] || "neutral"} testId="wi-import-status">{job.status.replace("_", " ")}</Pill>
        {running ? (
          <button className={btnGhost} disabled={busy} onClick={() => act("/admin/wi/import/pause", "Import paused")} data-testid="wi-import-pause-btn"><Pause size={14} weight="fill" /> Pause</button>
        ) : (
          <button className={btnPrimary} disabled={busy} onClick={() => act("/admin/wi/import", "Import started")} data-testid="wi-import-start-btn">
            <Play size={14} weight="fill" /> {job.status === "not_started" ? "Start import" : job.status === "done" ? "Re-run (new claims only)" : "Resume import"}
          </button>
        )}
      </>
    }>
      <p className="text-xs text-neutral-500 mb-4">Read-only: reads Warranty Claim 2.0 and the approval register. Nothing is written back to Monday.com, emailed or approved.</p>
      <Stepper job={job} />
      <div className="mt-4">
        <div className="flex justify-between text-xs text-neutral-400 mb-1.5">
          <span>Repair orders read by AI</span>
          <span className="tabular-nums" data-testid="wi-extract-progress">{num(ex.done || 0)} / {num(totalPdf)} · {pctDone}%{ex.failed ? ` · ${ex.failed} failed (retrying)` : ""}</span>
        </div>
        <div className="h-2 rounded-full bg-white/[0.05] overflow-hidden"><div className="h-full bg-[#73bf44] transition-[width] duration-500" style={{ width: `${pctDone}%` }} /></div>
      </div>
      <div className="grid grid-cols-4 gap-2 mt-4 text-center">
        {[["Claims", st.claims], ["Repair lines", st.lines], ["Faults", st.faults], ["Categories", st.categories]].map(([l, v]) => (
          <div key={l} className="bg-black/30 rounded-lg py-2"><div className="font-heading font-bold text-white tabular-nums">{num(v)}</div><div className="text-[0.62rem] text-neutral-500 uppercase tracking-wider">{l}</div></div>
        ))}
      </div>
      {job.error && <p className="text-xs text-red-400 mt-3" data-testid="wi-import-error">{job.error}</p>}
      {job.log?.length > 0 && (
        <ul className="mt-4 max-h-28 overflow-y-auto text-[0.7rem] text-neutral-500 space-y-0.5 font-mono" data-testid="wi-import-log">
          {[...job.log].reverse().slice(0, 8).map((l, i) => <li key={i}><span className="text-neutral-600">{new Date(l.at).toLocaleTimeString("en-AU", { timeZone: "Australia/Brisbane" })}</span> {l.msg}</li>)}
        </ul>
      )}
    </Card>
  );
}
